import storage from '@/utils/storage'

/**
 * A login journey groups the separate auth requests a customer makes in one
 * sign-in attempt (identity check, then password / claim / magic link) under a
 * single id (DF-352). The backend keys one login_attempts row per journey off
 * this id, sent as the X-Login-Journey header, so the whole flow reads from one
 * row instead of a row per step.
 *
 * The id lives in sessionStorage: it survives the step-to-step navigation of a
 * visit but not a new tab or a browser restart, which is exactly the boundary
 * of one journey. A fresh visit to the login page starts a new one.
 */
const KEY = 'login_journey_id'

const newId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the manual id
  }

  return `lj-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

/** Begin a new journey and return its id. Call when the login flow starts. */
export const startLoginJourney = (): string => {
  const id = newId()
  try {
    storage.setSession(KEY, id)
  } catch {
    // sessionStorage unavailable (private mode, etc.): the header just goes
    // out per-request instead, which the backend still handles.
  }
  return id
}

/** The current journey id, starting one if none is in progress. */
export const getLoginJourneyId = (): string => {
  try {
    const existing = storage.getSession(KEY) as string | null
    if (existing) {
      return existing
    }
  } catch {
    // ignore and start a fresh one
  }
  return startLoginJourney()
}

/** End the current journey (on a terminal outcome), so the next is separate. */
export const endLoginJourney = (): void => {
  try {
    storage.removeSession(KEY)
  } catch {
    // nothing to clear
  }
}

/** The header to send on a login-journey request. */
export const loginJourneyHeaders = (): Record<string, string> => ({
  'X-Login-Journey': getLoginJourneyId(),
})
